import { UserSubProfession } from '../../enums/UserProfession';
import { TREATMENT_LABELS, TREATMENT_MESSAGES } from './likelyToFillCopy';

/**
 * Likely to Fill label for a posted rate in the treatment arm of the GOTU-13065
 * A/B test, as the office apps (dental-portal GOTU-19796, mobile GOTU-19795)
 * show it, so staff posting for an office in that arm see what it would see.
 * Control offices get the admin panel's rate picker unchanged.
 *
 * Measured against the rounded recommended (average) rate, never the GOTU-972
 * enhanced rate.
 */

const LIMIT_KEYS = ['veryLikely', 'likely', 'lessLikely', 'warning'];

const HYGIENIST_DENTIST_LIMITS = Object.freeze({
  veryLikely: 5,
  likely: 0,
  lessLikely: -3,
  warning: -6,
});

const ASSISTANT_FRONT_DESK_LIMITS = Object.freeze({
  veryLikely: 3,
  likely: 0,
  lessLikely: -2,
  warning: -5,
});

/**
 * Lowest whole-dollar gap (posted minus recommended) for each treatment band.
 * Identical to the flag's `treatment` payload; used when it is missing or invalid.
 */
export const DEFAULT_LIKELY_TO_FILL_BANDS = Object.freeze({
  RDH: HYGIENIST_DENTIST_LIMITS,
  DN: HYGIENIST_DENTIST_LIMITS,
  DA: ASSISTANT_FRONT_DESK_LIMITS,
  EFDA: ASSISTANT_FRONT_DESK_LIMITS,
  FD: ASSISTANT_FRONT_DESK_LIMITS,
});

const PROFESSIONS = Object.keys(DEFAULT_LIKELY_TO_FILL_BANDS);

const COPY_GROUP = {
  RDH: 'hygienistDentist',
  DN: 'hygienistDentist',
  DA: 'assistantFrontDesk',
  EFDA: 'assistantFrontDesk',
  FD: 'assistantFrontDesk',
};

const TREATMENT_TONE = {
  veryLikely: 'positive',
  likely: 'positive',
  lessLikely: 'neutral',
  warning: 'warning',
  unlikely: 'negative',
};

// Strictly descending integers with lessLikely ≤ 0, so the bands can't overlap
// and the "$X+" in the alerts stays a positive whole-dollar amount.
const isValidLimits = (limits) =>
  limits != null &&
  LIMIT_KEYS.every((key) => Number.isInteger(limits[key])) &&
  limits.veryLikely > limits.likely &&
  limits.likely > limits.lessLikely &&
  limits.lessLikely > limits.warning &&
  limits.lessLikely <= 0;

const toObject = (payload) => {
  if (typeof payload !== 'string') return payload;
  try {
    return JSON.parse(payload);
  } catch {
    return null;
  }
};

/**
 * Reads the flag's treatment payload (a JSON string, or an already-parsed
 * object). Each profession is validated on its own, so one bad entry falls back
 * to its default without dropping the others.
 *
 * @param {unknown} payload
 * @returns {typeof DEFAULT_LIKELY_TO_FILL_BANDS}
 */
export const parseLikelyToFillBands = (payload) => {
  const value = toObject(payload);
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return DEFAULT_LIKELY_TO_FILL_BANDS;
  }

  return PROFESSIONS.reduce((bands, profession) => {
    const limits = value[profession];
    return {
      ...bands,
      [profession]: isValidLimits(limits)
        ? Object.fromEntries(LIMIT_KEYS.map((key) => [key, limits[key]]))
        : DEFAULT_LIKELY_TO_FILL_BANDS[profession],
    };
  }, {});
};

// The gap needs a real rate and a recommendation that rounds above zero.
const toComparable = (rate, recommendedRate) => {
  const recommended = Math.round(recommendedRate);
  if (!Number.isFinite(rate) || rate <= 0) return null;
  if (!Number.isFinite(recommended) || recommended <= 0) return null;
  return { rate, recommended };
};

const shortfallBelow = (lowestGap) => `$${1 - lowestGap}+`;

/**
 * @typedef {Object} ILikelyToFillBand
 * @property {'veryLikely'|'likely'|'lessLikely'|'warning'|'unlikely'} band
 * @property {'positive'|'neutral'|'warning'|'negative'} tone
 * @property {string} label
 * @property {string} [message] - warning/unlikely only: the alert under the label
 */

/**
 * Treatment arm: the label (and, for the two lowest bands, the alert) for the
 * whole-dollar gap between the rate and the rounded recommendation. Null, so no
 * label, for a profession outside the five banded ones.
 *
 * @param {Object} params
 * @param {number} params.rate - Posted hourly rate
 * @param {number|null} params.recommendedRate - The office's recommended average for the profession
 * @param {string} params.profession
 * @param {string|null} [params.subProfession]
 * @param {typeof DEFAULT_LIKELY_TO_FILL_BANDS} [params.bands]
 * @returns {ILikelyToFillBand|null}
 */
export const getLikelyToFillBand = ({
  rate,
  recommendedRate,
  profession,
  subProfession = null,
  bands = DEFAULT_LIKELY_TO_FILL_BANDS,
}) => {
  const comparable = toComparable(rate, recommendedRate);
  const bandKey = subProfession === UserSubProfession.EFDA ? UserSubProfession.EFDA : profession;
  const limits = bands[bandKey];
  if (!comparable || !limits) return null;

  const gap = comparable.rate - comparable.recommended;
  const copyGroup = COPY_GROUP[bandKey];

  let band = 'unlikely';
  if (gap >= limits.veryLikely) band = 'veryLikely';
  else if (gap >= limits.likely) band = 'likely';
  else if (gap >= limits.lessLikely) band = 'lessLikely';
  else if (gap >= limits.warning) band = 'warning';

  const indicator = { band, tone: TREATMENT_TONE[band], label: TREATMENT_LABELS[band] };

  if (band === 'warning') {
    return {
      ...indicator,
      message: TREATMENT_MESSAGES.warning[copyGroup](shortfallBelow(limits.lessLikely)),
    };
  }
  if (band === 'unlikely') {
    return {
      ...indicator,
      message: TREATMENT_MESSAGES.unlikely[copyGroup](shortfallBelow(limits.warning)),
    };
  }
  return indicator;
};
